import { generateCampaign } from "@/helpers/API/requests/customer";
import { TGenerateCampaignResponse } from "@/helpers/API/requests/interfaces/customer";
import {
  getCurrentLocale,
  localeChangeEventName,
  TLocaleChangeEvent,
} from "@/helpers/locale";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

export const useGenerateCampaign = () => {
  const location = useLocation();
  const campaignToken = new URLSearchParams(location.search).get("campaign");
  const [campaignData, setCampaignData] =
    useState<TGenerateCampaignResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [campaignError, setCampaignError] = useState<string | null>(null);
  const [token, setToken] = useState("");
  /* The laboratory legal document is served in the locale asked for, so the
   * campaign has to be fetched again whenever the journey language changes.
   * LocaleProvider is mounted below CampaignProvider (see AppRouter), hence the
   * window event rather than a context read. */
  const [locale, setLocale] = useState(getCurrentLocale);
  const loadedTokenRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);

  useEffect(function listenToLocaleChange() {
    const update = (event: Event) =>
      setLocale((event as TLocaleChangeEvent).detail.locale);
    window.addEventListener(localeChangeEventName, update);
    return () => window.removeEventListener(localeChangeEventName, update);
  }, []);

  useEffect(() => {
    if (!campaignToken) {
      setIsLoading(false);
      return;
    }

    /* Only the first fetch of a given campaign may raise isLoading: it feeds
     * campaignLoading, which gates the whole AuthProvider subtree. Raising it
     * on a locale refetch would unmount the journey and lose the customer's
     * progress. */
    const isInitialFetch = loadedTokenRef.current !== campaignToken;
    if (isInitialFetch) setIsLoading(true);

    /* Two quick language changes race: only the latest response may win. */
    const requestId = ++requestIdRef.current;

    generateCampaign(campaignToken, locale)
      .then((data) => {
        if (requestId !== requestIdRef.current) return;

        const { token } = data;

        loadedTokenRef.current = campaignToken;
        setCampaignData(data);
        setIsLoading(false);
        setToken(token);
      })
      .catch((data) => {
        if (requestId !== requestIdRef.current) return;

        setIsLoading(false);
        if (data.response.data.error) {
          setCampaignError(data.response.data.error);
        }
      });
  }, [campaignToken, locale]);

  return { campaignData, isLoading, token, campaignError };
};
